import { Loading, Section, SubmitButton, TextField, isEmail } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { Form, Formik, FormikErrors } from "formik";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useRegion } from "../../region/RegionProvider";
import { ConsentFormFields } from "../../registration/consents/ConsentFormFields";
import { ConsentValues } from "../../registration/provider/registrationFormProviderContext";
import { toConsentErrors, toConsentValues } from "../../registration/registrationFormUtils";
import { RenderRegistrationBlock } from "../../registration/RenderRegistrationBlock";
import { registrationFormService } from "../../shared/api/registrationForms";
import { RegistrationFormResource } from "../../shared/types/registrationForm";

export interface EmailGateFormValues {
	consents: ConsentValues;
	email: string;
}

interface Props {
	registrationForm: RegistrationFormResource;
	onSubmit: (value: EmailGateFormValues) => Promise<void>;
}

const StyledSection = styled(Section)`
	padding: ${({ theme }) => theme.spacing(4)};
`;

const ButtonWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
`;

export const EmailGateForm: FC<Props> = ({ registrationForm, onSubmit }) => {
	const { t } = useTranslation();
	const { regionId } = useRegion();
	const { data: consents = [], isLoading } = useQuery({
		queryKey: [...registrationFormService.keys.consentsForEmailGatePage(registrationForm.id), regionId],
		queryFn: () =>
			registrationFormService.consentsForEmailGatePage(registrationForm.id, regionId).then((x) => x.data.data),
	});

	const initialFormValues: EmailGateFormValues = useMemo(
		() => ({
			consents: toConsentValues(consents),
			email: "",
		}),
		[consents],
	);

	const validate = useCallback(
		(values: EmailGateFormValues) => {
			const errors: FormikErrors<EmailGateFormValues> = {};
			const consentsErrors = toConsentErrors(consents, values.consents, t);
			if (Object.keys(consentsErrors).length > 0) {
				errors.consents = consentsErrors;
			}
			if (!values.email) {
				errors.email = t("Field is required");
			} else if (!isEmail(values.email)) {
				errors.email = t("Invalid email address");
			}
			return errors;
		},
		[t, consents],
	);

	if (isLoading) {
		return (
			<StyledSection color="content">
				<Loading />
			</StyledSection>
		);
	}

	return (
		<>
			{registrationForm.emailGateBlockId && <RenderRegistrationBlock blockId={registrationForm.emailGateBlockId} />}
			<StyledSection color="content">
				<Formik initialValues={initialFormValues} onSubmit={onSubmit} validate={validate}>
					<Form>
						<TextField name="email" required={true} label={t("E-Mail")} />
						{consents.length > 0 && <ConsentFormFields consents={consents} name="consents" />}
						<ButtonWrapper>
							<SubmitButton>{t("Submit")}</SubmitButton>
						</ButtonWrapper>
					</Form>
				</Formik>
			</StyledSection>
		</>
	);
};
